import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { 
  User, Mail, Phone, Calendar, MapPin, Shield, Ban, 
  CheckCircle, XCircle, AlertCircle, Eye, Package,
  Activity, Lock
} from 'lucide-react';
import axios from 'axios';
import { useCountry } from '../context/CountryContext';

const UserDetailsModal = ({ user, isOpen, onClose, onUpdate }) => {
  const { formatCurrency } = useCountry();
  const [loading, setLoading] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [suspendDays, setSuspendDays] = useState(7);
  const [suspendReason, setSuspendReason] = useState('');
  const [details, setDetails] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  React.useEffect(() => {
    if (isOpen && user) {
      fetchUserDetails();
    }
  }, [isOpen, user]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/user-management/${user.id}`,
        { headers: getAuthHeader() }
      );
      setDetails(response.data);
    } catch (err) {
      console.error('Error fetching user details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    if (!window.confirm(`Are you sure you want to ${status} this user?`)) return;

    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/user-management/${user.id}/status`,
        { status },
        { headers: getAuthHeader() }
      );
      alert(`User ${status} successfully`);
      onUpdate();
      onClose();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const handleBan = async () => {
    if (!banReason.trim()) {
      alert('Please provide a reason for banning');
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/user-management/${user.id}/ban`,
        { reason: banReason, permanent: true },
        { headers: getAuthHeader() }
      );
      alert('User banned successfully');
      onUpdate();
      onClose();
    } catch (err) {
      alert('Failed to ban user');
    }
  };

  const handleSuspend = async () => {
    if (!suspendReason.trim()) {
      alert('Please provide a reason for suspension');
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/user-management/${user.id}/suspend`,
        { reason: suspendReason, days: suspendDays },
        { headers: getAuthHeader() }
      );
      alert(`User suspended for ${suspendDays} days`);
      onUpdate();
      onClose();
    } catch (err) {
      alert('Failed to suspend user');
    }
  };

  const handleUpdateVerification = async (field, value) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/user-management/${user.id}/verification`,
        { [field]: value },
        { headers: getAuthHeader() }
      );
      alert('Verification updated successfully');
      fetchUserDetails();
    } catch (err) {
      alert('Failed to update verification');
    }
  };

  if (!user || !details) return null;

  const statusColor = {
    active: 'bg-green-500',
    suspended: 'bg-yellow-500',
    banned: 'bg-red-500',
    inactive: 'bg-gray-500'
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>User Details: {details.user.name}</span>
            <Badge className={statusColor[details.user.status]}>
              {details.user.status.toUpperCase()}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="info" className="mt-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="info">Info</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="listings">Listings</TabsTrigger>
            <TabsTrigger value="actions">Actions</TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="info" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium">{details.user.name}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{details.user.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{details.user.phone}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Joined</p>
                    <p className="font-medium">
                      {new Date(details.user.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Verification Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Verification Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    {details.user.email_verified ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
                    <span>Email Verified</span>
                  </span>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleUpdateVerification('email_verified', !details.user.email_verified)}
                  >
                    Toggle
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    {details.user.phone_verified ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
                    <span>Phone Verified</span>
                  </span>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleUpdateVerification('phone_verified', !details.user.phone_verified)}
                  >
                    Toggle
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    {details.user.verified_badge ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
                    <span>Verified Badge</span>
                  </span>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleUpdateVerification('verified_badge', !details.user.verified_badge)}
                  >
                    Toggle
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Statistics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Total Listings</p>
                    <p className="text-2xl font-bold">{details.statistics.total_listings}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Active Listings</p>
                    <p className="text-2xl font-bold">{details.statistics.active_listings}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Views</p>
                    <p className="text-2xl font-bold">{details.statistics.total_views}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Seller Rating</p>
                    <p className="text-2xl font-bold">{details.user.seller_rating.toFixed(1)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Login History</CardTitle>
              </CardHeader>
              <CardContent>
                {details.activity_logs && details.activity_logs.length > 0 ? (
                  <div className="space-y-3">
                    {details.activity_logs.map((log, index) => (
                      <div key={index} className="flex items-start space-x-3 pb-3 border-b last:border-0">
                        <Activity className="h-4 w-4 text-blue-500 mt-1" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{log.action || 'Login'}</p>
                          <p className="text-xs text-gray-500">
                            IP: {log.ip_address} | {new Date(log.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No activity logs available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Listings</CardTitle>
              </CardHeader>
              <CardContent>
                {details.recent_listings && details.recent_listings.length > 0 ? (
                  <div className="space-y-3">
                    {details.recent_listings.map((listing, index) => (
                      <div key={index} className="flex items-center justify-between pb-3 border-b last:border-0">
                        <div>
                          <p className="font-medium">{listing.title}</p>
                          <p className="text-sm text-gray-500">
                            {formatCurrency(parseInt(listing.price))}
                          </p>
                        </div>
                        <Badge>{listing.status}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No listings yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Actions Tab */}
          <TabsContent value="actions" className="space-y-4">
            {/* Status Controls */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Account Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {details.user.status !== 'active' && (
                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={() => handleUpdateStatus('active')}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Activate Account
                  </Button>
                )}
                
                {/* Ban Section */}
                <div className="border rounded-lg p-4 space-y-3">
                  <Label>Ban User</Label>
                  <Textarea 
                    placeholder="Reason for banning..."
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                  />
                  <Button 
                    variant="destructive" 
                    className="w-full"
                    onClick={handleBan}
                  >
                    <Ban className="h-4 w-4 mr-2" />
                    Ban Permanently
                  </Button>
                </div>

                {/* Suspend Section */}
                <div className="border rounded-lg p-4 space-y-3">
                  <Label>Suspend User</Label>
                  <Input 
                    type="number"
                    placeholder="Days"
                    value={suspendDays}
                    onChange={(e) => setSuspendDays(parseInt(e.target.value))}
                    min="1"
                  />
                  <Textarea 
                    placeholder="Reason for suspension..."
                    value={suspendReason}
                    onChange={(e) => setSuspendReason(e.target.value)}
                  />
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={handleSuspend}
                  >
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Suspend for {suspendDays} days
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailsModal;
